import { FC, useState } from 'react';
import './Weather.scss';
import { DailyWeatherV4, LocationV4, WeatherApiResponse, } from '@moment/net/accuweather-types.ts';
import iconMap from '@moment/components/Weather/iconMap.ts';
import { useTranslation } from 'react-i18next';

export interface WeatherProps {
  current: WeatherApiResponse | null;
  location: LocationV4 | null;
  daily: DailyWeatherV4[] | null;
  night: boolean;
}

const Weather: FC<WeatherProps> = ({ current, location, daily, night }) => {
  const { t } = useTranslation();
  const [showFiveDay, setShowFiveDay] = useState(false);
  const [showCelsius, setShowCelsius] = useState(false);

  const daysOfWeek = [
    t('Sunday'),
    t('Monday'),
    t('Tuesday'),
    t('Wednesday'),
    t('Thursday'),
    t('Friday'),
    t('Saturday'),
  ];

  const {
    temperature,
    temperatureMax,
    temperatureMin,
    temperatureFeelsLike,
    iconCode,
  } = current || {};

  const { city, state, zipcode } = location || {};

  const getDoW = (index: number): string => {
    const today = new Date().getDay();
    const day = daysOfWeek[(today + index) % 7];
    // translate day abd truncate to 3 chars
    return day ? t(day).substring(0, 3) : '';
  };

  const getIconUrl = (iconCode: number): string | null => {
    return iconCode && iconMap[iconCode]?.iconUrl !== ''
      ? iconMap[iconCode].iconUrl
      : null;
  };

  const getWeatherDescription = (iconCode: number): string => {
    return iconCode && iconMap[iconCode]?.description
      ? t(iconMap[iconCode].description)
      : t('Unknown');
  };

  const clickCelsius = () => {
    setShowCelsius(true);
  };

  const clickFahrenheit = () => {
    setShowCelsius(false);
  };

  const getWeatherTemp = (temp: number): number => {
    // default is F, return if set to C
    return showCelsius && temp ? Math.floor((temp - 32) * (5 / 9)) : temp;
  };

  const toggleFiveDay = () => {
    setShowFiveDay(!showFiveDay);
  };

  const weatherClassName = night
    ? 'weather--container--night'
    : 'weather--container--day';

  const handleNoDataButtonClick = () => {
    window.open('https://www.accuweather.com/', '_blank');
  };

  if (!temperature) {
    return (
      <div
        className={`weather--container--no-data ${weatherClassName}--no-data`}
      >
        <div className="body--container">
          <div className="weather--header">{t('weatherHeaderNoData')}</div>
          <div className="weather--body">
            <div className="body--left">{t('weatherBodyNoData')}</div>
            <div className="body--right">
              <button onClick={handleNoDataButtonClick}>
                {t('weatherButton')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`weather--container ${weatherClassName}`}>
      <div className="weather--header">
        <div className="weather--location truncate">
          {city}, {state}
        </div>
        <div className="weather--link">
          <img
            src="/assets/images/accuweather-logo.svg"
            alt="accuweather.com"
          />
        </div>
      </div>
      <div className="weather--container-main">
        <div className="main--left">
          <div className="weather--icon">
            {getIconUrl(iconCode) && (
              <img
                src={getIconUrl(iconCode)}
                alt={getWeatherDescription(iconCode)}
              />
            )}
          </div>
        </div>
        <div className="main--right">
          <div className="weather--temperature flex justify-end">
            <div className="current-temperature">
              <div className="current-temperature-left">
                {getWeatherTemp(temperature)}
              </div>
              <div className="current-temperature-right">
                <div
                  onClick={clickFahrenheit}
                  className={
                    showCelsius ? 'weather--temperature--scale--unselected' : ''
                  }
                >
                  °F
                </div>
                <div>|</div>
                <div
                  onClick={clickCelsius}
                  className={
                    !showCelsius
                      ? 'weather--temperature--scale--unselected'
                      : ''
                  }
                >
                  °C
                </div>
              </div>
            </div>
            <div className="max-min-temperature">
              <div className="max-temperature">
                {getWeatherTemp(temperatureMax)}°
              </div>
              <div className="max-temperature-up-arrow"></div>
              <div className="min-temperature">
                {getWeatherTemp(temperatureMin)}°
              </div>
              <div className="min-temperature-down-arrow"></div>
            </div>
          </div>
        </div>
      </div>
      <div className="weather--conditions">
        <div className="weather--conditions-left">
          <div className="weather--description truncate">
            {getWeatherDescription(iconCode)}
          </div>
        </div>
        <div className="weather--conditions-right">
          RealFeel® {getWeatherTemp(temperatureFeelsLike)}°
        </div>
      </div>
      {daily && (
        <div className="weather--container-footer">
          <div className="weather--fiveday-header" onClick={toggleFiveDay}>
            <div className="weather--fiveday-description">
              {t('fiveDayForecast')}
            </div>
            <div
              className={`${!showFiveDay ? 'weather--fiveday-arrowup' : 'weather--fiveday-arrowdown'}`}
            ></div>
          </div>
          {showFiveDay && (
            <div className="five-day--container">
              {daily.map((d, index) => {
                const dow = getDoW(index);
                const iconCode = getIconUrl(d.iconCode);
                return index < 5 ? (
                  <div className="five-day--row">
                    <div className="five-day-dow">{dow}</div>
                    <div className="five-day-icon">
                      {iconCode && (
                        <img
                          src={iconCode}
                          className="mx-auto"
                          alt={getWeatherDescription(d.iconCode)}
                        />
                      )}
                    </div>
                    <div className="five-day-weather">
                      {getWeatherTemp(d.temperatureMax)}°
                    </div>
                  </div>
                ) : null;
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Weather;
